import React, { useEffect, useCallback, useState } from 'react';
import BotBuilderService from '../../services/BotBuilderService/BotBuilderService';
import IntentSummary from '../../types/IntentSummary';
import SearchableDropdown, {
  type DropdownItem
} from '../SearchableDropdown/SearchableDropdown';

interface IntentsAutoCompleteProps {
  intentId?: number | null;
  intentName?: string | null;
  onSelectId?: (intentId: number | null) => void;
  onNavigate?: (intentId: number) => void;
  showClearButton?: boolean;
  className?: string;
  triggerClassName?: string;
  chatbotId: number;
  disabled?: boolean;
}

function IntentsAutoComplete({
  intentId,
  intentName,
  onSelectId,
  onNavigate,
  showClearButton = true,
  className = '',
  triggerClassName,
  chatbotId,
  disabled = false
}: Readonly<IntentsAutoCompleteProps>): React.ReactElement {
  const [intents, setIntents] = useState<IntentSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentQuery, setCurrentQuery] = useState<string>('');

  const [selectedIntent, setSelectedIntent] = useState<DropdownItem | null>(
    null
  );

  useEffect(() => {
    if (intentId && intentName) {
      setSelectedIntent({
        id: intentId,
        label: intentName
      });
    } else {
      setSelectedIntent(null);
    }
  }, [intentId, intentName]);

  const loadIntents = useCallback(
    async (query: string, page: number, reset: boolean) => {
      if (!chatbotId) return;

      setIsLoading(true);
      try {
        const res = await BotBuilderService.getIntents({
          chatbotId,
          query,
          page,
          resultsPerPage: 100,
          signal: new AbortController().signal,
          exact: false,
          nameOnly: true
        });

        if (reset) {
          setIntents(res.results);
        } else {
          setIntents((prev) => [...prev, ...res.results]);
        }

        setHasMore(
          res.results.length === 100 && (page + 1) * 100 < res.numberOfResults
        );
        setCurrentPage(page);
        setCurrentQuery(query);
      } catch (error) {
        console.error('Error loading intents:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [chatbotId]
  );

  const handleSearch = useCallback(
    async (query: string) => {
      await loadIntents(query, 0, true);
    },
    [loadIntents]
  );

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    await loadIntents(currentQuery, currentPage + 1, false);
  }, [hasMore, isLoading, loadIntents, currentQuery, currentPage]);

  const handleSelect = useCallback(
    (item: DropdownItem) => {
      const selectedIntentId = typeof item.id === 'number' ? item.id : null;

      // Skip if selecting the same intent that's already selected
      if (selectedIntentId === intentId) {
        return;
      }

      setSelectedIntent(item);
      onSelectId?.(selectedIntentId);

      if (onNavigate && selectedIntentId) {
        onNavigate(selectedIntentId);
      }
    },
    [onSelectId, onNavigate, intentId]
  );

  const handleClear = useCallback(() => {
    setSelectedIntent(null);
    onSelectId?.(null);
  }, [onSelectId]);

  const dropdownItems: DropdownItem[] = (intents || []).map((intentItem) => ({
    id: intentItem.id,
    label: intentItem.intentName
  }));

  return (
    <div className={className}>
      <SearchableDropdown
        items={dropdownItems}
        selectedItem={selectedIntent}
        onSelect={handleSelect}
        onSearch={handleSearch}
        onLoadMore={handleLoadMore}
        onClear={handleClear}
        placeholder='Select a question'
        searchPlaceholder='Search questions…'
        emptyText='No results found'
        loading={isLoading}
        hasMore={hasMore}
        showClearButton={showClearButton}
        triggerClassName={triggerClassName}
        className='w-full'
        disabled={disabled}
      />
    </div>
  );
}

export default IntentsAutoComplete;
